import type {
  InboundInfo,
  InboundItem,
} from "@/hooks/roster-craete/useRosterSchedule";

/** 구형 `{ inbound_list }` · 신형 `inbound` 배열 직접 — 스케줄·발행 스냅샷 공통 */
export function inboundItemsFromInboundField(
  inbound: InboundInfo | InboundItem[] | undefined
): InboundItem[] {
  if (inbound == null) return [];
  if (Array.isArray(inbound)) return inbound;
  return inbound.inbound_list ?? [];
}
import type { Roster } from "@/types/snapshot-type";
import type { IssuedSnapshotAssignmentsRoot } from "@vTypes/snapshot-type";
import { normalizeIssuedAssignmentEntries } from "./mergeIssuedSnapshotAssignments";
import { computeAssignmentStarts } from "./computeAssignmentStarts";
import type { AssignmentStartInfo } from "./types";

export type ComputeInboundAssignmentStartsParams = {
  scheduleYear: number;
  scheduleMonth: number;
  schedulePayloadYear?: number;
  schedulePayloadMonth?: number;
};

/**
 * 근무표 만들기·발행 조회 공통: 페이로드 월과 뷰 월이 같을 때만 `inbound` 항목들로 기간바 메타 계산.
 * (`{ inbound_list }` 또는 배열 `inbound: [...]` 모두 수용)
 */
export function computeInboundAssignmentStarts(
  inbound: InboundInfo | InboundItem[] | undefined,
  params: ComputeInboundAssignmentStartsParams
): AssignmentStartInfo[] {
  const sy = params.scheduleYear ?? 0;
  const sm = params.scheduleMonth ?? 0;
  const py = params.schedulePayloadYear;
  const pm = params.schedulePayloadMonth;
  const payloadMatchesView =
    py != null &&
    pm != null &&
    Number.isFinite(py) &&
    Number.isFinite(pm) &&
    py === sy &&
    pm === sm;
  const items = payloadMatchesView
    ? inboundItemsFromInboundField(inbound)
    : [];
  return computeAssignmentStarts(items, sy, sm);
}

/**
 * 스냅샷/스케줄 API `assignments[]` 원본 → `InboundItem[]`.
 * 이미 `startDate` 형태면 그대로 통과시키려면 호출부에서 분기.
 */
export function inboundItemsFromIssuedAssignmentList(
  raw: unknown[] | null | undefined
): InboundItem[] {
  if (!raw?.length) return [];
  const list = normalizeIssuedAssignmentEntries(raw);
  return list.map((e) => ({
    startDate: e.start_date,
    endDate: e.end_date,
    reason: e.reason,
    target_group_id: e.target_group_id,
    target_group_name: e.target_group_name,
  }));
}

/**
 * 발행 스냅샷 루트 `assignments[nurseId]` → `InboundItem[]`.
 */
export function inboundItemsFromIssuedAssignmentsForNurse(
  assignments: IssuedSnapshotAssignmentsRoot | null | undefined,
  nurseId: string
): InboundItem[] {
  if (!assignments || nurseId === "") return [];
  const raw = assignments[String(nurseId)];
  return inboundItemsFromIssuedAssignmentList(
    Array.isArray(raw) ? (raw as unknown[]) : []
  );
}

/** 발행 조회 UI 테스트용 — `roster.nurses[targetIndex]` 에 병동이동 inbound 한 건 부착 */
export function rosterWithInboundDummyAtIndex(
  roster: Roster,
  year: number,
  month: number,
  nurseIndex = 1
): Roster {
  const ym = `${year}-${String(month).padStart(2, "0")}`;
  const nurses = roster.nurses.map((n, i) => {
    if (i !== nurseIndex) return n;
    return {
      ...n,
      is_inbound: true as const,
      inbound: {
        inbound_list: [
          {
            startDate: `${ym}-02`,
            endDate: null,
            reason: "병동이동",
            target_group_id: "G_DUMMY",
            target_group_name: "더미병동",
          },
        ],
      },
    };
  });
  return { ...roster, nurses };
}

type NurseRowWithInbound = {
  inbound?: InboundInfo;
  is_inbound?: true;
};

type NurseRowIdentified = NurseRowWithInbound & {
  nurse_id?: string;
  id?: string;
};

/**
 * `pre_monthlyNurseSchedule` 등 병합 후 행 중 `nurse_id` / `id` 가 일치하는 간호사에만 inbound 더미 부착.
 */
export function nursesWithInboundDummyForNurseId<T extends NurseRowIdentified>(
  nurses: T[],
  year: number,
  month: number,
  nurseId: string
): T[] {
  const target = String(nurseId);
  if (!target) return nurses;
  const ym = `${year}-${String(month).padStart(2, "0")}`;
  return nurses.map((n) => {
    const rowId = String(n.nurse_id ?? n.id ?? "");
    if (rowId !== target) return n;
    return {
      ...n,
      is_inbound: true as const,
      inbound: {
        inbound_list: [
          {
            startDate: `${ym}-02`,
            endDate: null,
            reason: "병동이동",
            target_group_id: "G_DUMMY",
            target_group_name: "더미병동",
          },
        ],
      },
    };
  });
}

/**
 * `pre_monthlyNurseSchedule` 등 **정렬·병합 후** 표 행 순서(`displayRowIndex`) 기준으로 더미 inbound 부착.
 * (스냅샷 원본 배열 인덱스와 표 행 순서는 다를 수 있음)
 */
export function nursesWithInboundDummyAtDisplayIndex<T extends NurseRowWithInbound>(
  nurses: T[],
  year: number,
  month: number,
  displayRowIndex: number
): T[] {
  if (nurses.length <= displayRowIndex) return nurses;
  const ym = `${year}-${String(month).padStart(2, "0")}`;
  return nurses.map((n, i) => {
    if (i !== displayRowIndex) return n;
    return {
      ...n,
      is_inbound: true as const,
      inbound: {
        inbound_list: [
          {
            startDate: `${ym}-02`,
            endDate: null,
            reason: "병동이동",
            target_group_id: "G_DUMMY",
            target_group_name: "더미병동",
          },
        ],
      },
    };
  });
}
